import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import IconStar from "@ui/central-icons/icons/IconStar";
import { clsx } from "clsx";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import NextImage from "next/image";

interface AudioPopoverCoverProps {
  categoryName?: string;
  categoryImageUrl?: string;
  activeTrackTitle?: string;
  inactiveTrackTitle?: string;
}

const WAVEFORM_HEIGHTS = [6, 10, 14, 18, 12, 8, 16, 10, 14, 6, 12, 18, 8, 14, 10, 6, 16, 12, 8, 14, 10, 18, 6, 12, 14];
// orange-400 from A17 palette; no equivalent token in v2 Tailwind config
const ACTIVE_STAR_COLOR = "#F58633";

function Waveform({ filledBars = 0 }: { filledBars?: number }) {
  return (
    <div className='tw-flex tw-gap-px tw-items-center tw-h-[18px]'>
      {WAVEFORM_HEIGHTS.map((h, i) => (
        <div
          key={i}
          className={clsx(
            "tw-w-[2px] tw-rounded-full tw-shrink-0",
            i < filledBars ? "tw-bg-black/60" : "tw-bg-black/20",
          )}
          style={{ height: `${h}px` }}
        />
      ))}
    </div>
  );
}

const AudioPopoverCover = ({
  categoryName,
  categoryImageUrl,
  activeTrackTitle,
  inactiveTrackTitle,
}: AudioPopoverCoverProps) => {
  return (
    <div className='tw-relative tw-h-[178px] tw-w-full tw-overflow-hidden tw-bg-gradient-to-b tw-from-cream tw-to-transparent'>
      {/* Category chip */}
      <div className='tw-absolute tw-left-6 tw-top-[60px] tw-flex tw-items-center tw-gap-2'>
        <div className='tw-size-6 tw-rounded-sm tw-overflow-hidden tw-bg-black/10 tw-shrink-0'>
          {categoryImageUrl && (
            <NextImage
              src={categoryImageUrl}
              alt={categoryName ?? ""}
              width={24}
              height={24}
              className='tw-size-full tw-object-cover'
            />
          )}
        </div>
        <span className='tw-whitespace-nowrap tw-type-xs tw-text-black'>{categoryName}</span>
      </div>

      {/* Active audio row (tilted) */}
      <div className='tw-absolute tw-left-4 tw-right-4 tw-top-[87px]'>
        <div className='-tw-rotate-2 tw-transform-gpu'>
          <div className='tw-bg-white tw-rounded-xl tw-px-2 tw-py-[5px] tw-flex tw-items-center tw-gap-3 tw-ring-1 tw-ring-black/10 tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]'>
            {/* Play/title group */}
            <div className='tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1'>
              <div className='tw-size-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-bg-black'>
                <IconPauseFill className={clsx(iconStrokeFix, "tw-size-[10px] tw-text-white")} aria-hidden />
              </div>
              <span className='tw-truncate tw-min-w-0 tw-type-xs tw-py-1'>{activeTrackTitle}</span>
            </div>
            {/* Waveform */}
            <div className='tw-shrink-0'>
              <Waveform filledBars={10} />
            </div>
            {/* Bookmark (filled, orange) */}
            <div className='tw-size-[30px] tw-flex tw-items-center tw-justify-center tw-shrink-0'>
              <IconStar
                className={clsx(iconStrokeFix, "tw-size-[15px]")}
                style={{ color: ACTIVE_STAR_COLOR }}
                aria-hidden
              />
            </div>
          </div>
        </div>
      </div>

      {/* Inactive audio row */}
      <div className='tw-absolute tw-left-4 tw-right-4 tw-top-[137px]'>
        <div className='tw-bg-transparent tw-rounded-xl tw-px-2 tw-py-[5px] tw-flex tw-items-center tw-gap-3'>
          <div className='tw-flex tw-items-center tw-gap-2 tw-min-w-0 tw-flex-1'>
            <div className='tw-size-6 tw-rounded-full tw-bg-black/[0.07] tw-flex tw-items-center tw-justify-center tw-shrink-0'>
              <IconPlayFill className={clsx(iconStrokeFix, "tw-size-[10px] tw-text-black tw-opacity-60")} aria-hidden />
            </div>
            <span className='tw-truncate tw-opacity-60 tw-type-xs tw-py-1'>{inactiveTrackTitle}</span>
          </div>
          <div className='tw-flex-1 tw-min-w-0 tw-opacity-40'>
            <Waveform />
          </div>
          <div className='tw-size-[30px] tw-flex tw-items-center tw-justify-center tw-shrink-0 tw-opacity-60'>
            <IconStar className={clsx(iconStrokeFix, "tw-size-[15px] tw-text-black")} />
          </div>
        </div>
      </div>
    </div>
  );
};

AudioPopoverCover.displayName = "AudioPopoverCover";

export { AudioPopoverCover };
export type { AudioPopoverCoverProps };
